/**
 * Procedural audio — zero asset files, all synthesised with WebAudio
 * oscillators. The {@link AudioContext} is created lazily and resumed on the
 * first user gesture so the game obeys browser autoplay rules.
 *
 * Every cue is a small stack of detuned oscillator "voices" routed through a
 * master gain, giving the neon SFX a thicker, more electric body without any
 * samples. Audio never affects the simulation, so it deliberately stays
 * outside the deterministic contract.
 */

/** One synth voice description. */
export interface Voice {
  /** Start frequency, Hz. */
  freq: number;
  /** Optional glide target frequency, Hz. */
  freqEnd?: number;
  /** Duration in seconds. */
  dur: number;
  /** Oscillator waveform. */
  type: OscillatorType;
  /** Peak gain (0..1). */
  gain: number;
  /** Start delay in seconds (for arpeggios). */
  delay?: number;
  /** Detune in cents — when set, a second layered oscillator thickens the voice. */
  detune?: number;
  /** Attack time in seconds (default 0.01). */
  attack?: number;
}

/** Every sound cue the game can request. */
export type SoundName =
  | 'fire-pulse'
  | 'fire-arc'
  | 'fire-cryo'
  | 'fire-mortar'
  | 'fire-sniper'
  | 'hit'
  | 'armor-hit'
  | 'shield-hit'
  | 'shield-break'
  | 'death'
  | 'corrode'
  | 'emp'
  | 'patch-heal'
  | 'place'
  | 'upgrade'
  | 'sell'
  | 'error'
  | 'wave-start'
  | 'wave-clear'
  | 'life-lost'
  | 'ability-overcharge'
  | 'ability-airstrike'
  | 'ability-emp'
  // Sprint 51 (polish-s51, docs/AUDIO_DESIGN.md §3.1 / §3.2 / §3.3 — additional
  // ability cues beyond Sprint 32 / 35). The Overcharge close stinger fires on
  // window-end; the Airstrike scorch hum lingers after the impact; the EMP
  // synergy thump layers in when EMP is fired during an active Overcharge.
  // The Cryo brittle paint cue rides its own per-enemy throttle (see
  // `Audio.playCryoBrittle`).
  | 'overcharge-end'
  | 'airstrike-scorch'
  | 'emp-synergy-thump'
  | 'cryo-brittle'
  | 'boss-incoming'
  | 'boss-spawn'
  | 'boss-phase'
  | 'boss-drain-charge'
  | 'boss-drain-emit'
  | 'boss-death'
  | 'win'
  | 'lose';

/** Named SFX → one or more synth voices. */
const SOUNDS: Record<SoundName, Voice[]> = {
  // Pulse Turret — a tight, bright zap with a sub thump.
  'fire-pulse': [
    { freq: 1040, freqEnd: 460, dur: 0.09, type: 'square', gain: 0.16, detune: 14 },
    { freq: 180, freqEnd: 70, dur: 0.07, type: 'sine', gain: 0.14 },
  ],
  // Arc Coil — a rising electric crackle with a detuned shimmer.
  'fire-arc': [
    { freq: 260, freqEnd: 1500, dur: 0.14, type: 'sawtooth', gain: 0.13, detune: 22 },
    { freq: 620, freqEnd: 1900, dur: 0.1, type: 'triangle', gain: 0.08, delay: 0.02 },
  ],
  // Cryo Emitter — a crystalline frost shimmer that sinks.
  'fire-cryo': [
    { freq: 1320, freqEnd: 680, dur: 0.16, type: 'sine', gain: 0.12, detune: 18 },
    { freq: 2100, freqEnd: 1500, dur: 0.1, type: 'triangle', gain: 0.05, delay: 0.02 },
  ],
  // Splash Mortar — a deep launch thunk under a short blast boom.
  'fire-mortar': [
    { freq: 150, freqEnd: 54, dur: 0.22, type: 'sine', gain: 0.2 },
    { freq: 90, freqEnd: 38, dur: 0.3, type: 'sawtooth', gain: 0.14, delay: 0.05, detune: 30 },
  ],
  // Sniper Rail — a sharp, high "crack" over a tight sub.
  'fire-sniper': [
    { freq: 2600, freqEnd: 620, dur: 0.08, type: 'square', gain: 0.16, detune: 12 },
    { freq: 240, freqEnd: 60, dur: 0.1, type: 'sine', gain: 0.12 },
  ],
  hit: [{ freq: 300, freqEnd: 200, dur: 0.04, type: 'square', gain: 0.08 }],
  // Armor clank — a short, bright metallic tick when a Cipher absorbs a hit.
  'armor-hit': [{ freq: 1700, freqEnd: 900, dur: 0.05, type: 'square', gain: 0.07 }],
  // Buffer shield absorb — a glassy "ting".
  'shield-hit': [
    { freq: 1900, dur: 0.07, type: 'sine', gain: 0.07, detune: 20 },
    { freq: 2750, dur: 0.05, type: 'triangle', gain: 0.04, delay: 0.01 },
  ],
  // Buffer shield break — a descending glassy "shatter".
  'shield-break': [
    { freq: 1500, freqEnd: 320, dur: 0.16, type: 'sawtooth', gain: 0.12, detune: 40 },
    { freq: 920, freqEnd: 210, dur: 0.12, type: 'square', gain: 0.07, delay: 0.02 },
  ],
  // Enemy death — a downward zap-crunch.
  death: [
    { freq: 220, freqEnd: 36, dur: 0.2, type: 'sawtooth', gain: 0.18, detune: 30 },
    { freq: 90, freqEnd: 30, dur: 0.16, type: 'square', gain: 0.1 },
  ],
  // Corrosion — a thin acid sizzle.
  corrode: [{ freq: 2400, freqEnd: 1500, dur: 0.12, type: 'sawtooth', gain: 0.05, detune: 40 }],
  // EMP — a staticky "fzzt" sweep with a high crackle.
  emp: [
    { freq: 130, freqEnd: 540, dur: 0.12, type: 'sawtooth', gain: 0.1, detune: 50 },
    { freq: 2050, freqEnd: 880, dur: 0.08, type: 'square', gain: 0.05, delay: 0.01 },
  ],
  // Patch repair — a soft two-note healing chime.
  'patch-heal': [
    { freq: 880, dur: 0.1, type: 'sine', gain: 0.07 },
    { freq: 1320, dur: 0.16, type: 'sine', gain: 0.06, delay: 0.06 },
  ],
  // Tower placed — a confident two-tone power-up.
  place: [
    { freq: 360, freqEnd: 540, dur: 0.07, type: 'triangle', gain: 0.18 },
    { freq: 540, freqEnd: 820, dur: 0.12, type: 'triangle', gain: 0.18, delay: 0.06 },
  ],
  // Tower upgraded — a bright triumphant three-step lift.
  upgrade: [
    { freq: 440, freqEnd: 660, dur: 0.08, type: 'triangle', gain: 0.18 },
    { freq: 660, freqEnd: 880, dur: 0.1, type: 'triangle', gain: 0.18, delay: 0.07 },
    { freq: 990, freqEnd: 1180, dur: 0.16, type: 'triangle', gain: 0.18, delay: 0.15, detune: 9 },
  ],
  sell: [
    { freq: 720, freqEnd: 300, dur: 0.13, type: 'triangle', gain: 0.18, detune: 10 },
  ],
  // Rejected action — a flat, dull buzz.
  error: [{ freq: 130, freqEnd: 96, dur: 0.16, type: 'square', gain: 0.16 }],
  // Wave start — a rising alarm sweep with a harmonic layer.
  'wave-start': [
    { freq: 180, freqEnd: 720, dur: 0.4, type: 'sawtooth', gain: 0.14, detune: 16 },
    { freq: 360, freqEnd: 1080, dur: 0.34, type: 'triangle', gain: 0.07, delay: 0.04 },
  ],
  // Wave cleared — a bright ascending triad.
  'wave-clear': [
    { freq: 523, dur: 0.12, type: 'triangle', gain: 0.2 },
    { freq: 659, dur: 0.13, type: 'triangle', gain: 0.2, delay: 0.09 },
    { freq: 880, dur: 0.22, type: 'triangle', gain: 0.2, delay: 0.18, detune: 8 },
  ],
  // Life lost — a harsh descending alarm.
  'life-lost': [
    { freq: 230, freqEnd: 80, dur: 0.32, type: 'square', gain: 0.2, detune: 24 },
    { freq: 460, freqEnd: 160, dur: 0.18, type: 'sawtooth', gain: 0.08 },
  ],
  // Overcharge — a rising power swell.
  'ability-overcharge': [
    { freq: 220, freqEnd: 880, dur: 0.42, type: 'sawtooth', gain: 0.16, detune: 18 },
    { freq: 440, freqEnd: 1320, dur: 0.34, type: 'triangle', gain: 0.09, delay: 0.05 },
  ],
  // Airstrike — an incoming whistle into a deep boom.
  'ability-airstrike': [
    { freq: 1600, freqEnd: 320, dur: 0.34, type: 'sine', gain: 0.12, detune: 12 },
    { freq: 120, freqEnd: 40, dur: 0.36, type: 'sawtooth', gain: 0.2, delay: 0.3, detune: 32 },
  ],
  // EMP Burst — a field-wide deep "fzzt".
  'ability-emp': [
    { freq: 90, freqEnd: 420, dur: 0.3, type: 'sawtooth', gain: 0.18, detune: 60 },
    { freq: 1800, freqEnd: 520, dur: 0.22, type: 'square', gain: 0.08, delay: 0.03 },
  ],
  // Sprint 51 — Overcharge window-close stinger (docs/AUDIO_DESIGN.md §3.1). A
  // soft click-and-decay on the tick the burst window expires: tells the
  // player "the window is closed" without staring at the HUD timer.
  'overcharge-end': [{ freq: 660, freqEnd: 220, dur: 0.08, type: 'square', gain: 0.06 }],
  // Sprint 51 — Airstrike scorch residual (docs/AUDIO_DESIGN.md §3.2). A short
  // sawtooth sizzle delayed past the impact thud so it reads as the burn
  // settling in. Plays once per Airstrike call.
  'airstrike-scorch': [
    { freq: 1600, freqEnd: 900, dur: 0.6, type: 'sawtooth', gain: 0.06, detune: 40, delay: 0.4 },
  ],
  // Sprint 51 — EMP synergy thump (docs/AUDIO_DESIGN.md §3.3). Layered onto the
  // base EMP cue *only* when Overcharge is also active — the combo lands as a
  // bigger, sub-bass-led discharge. Fired separately from `ability-emp` so the
  // standalone EMP retains its existing voice stack.
  'emp-synergy-thump': [{ freq: 70, freqEnd: 28, dur: 0.5, type: 'sine', gain: 0.22 }],
  // Sprint 51 — Cryo brittle paint cue (per-enemy throttled). A short crystalline
  // shimmer-crack: the moment a target gains the brittle mark from a Cryo pulse.
  // Sits at a low gain so a small burst of Cryo paints does not crowd `fire-cryo`.
  'cryo-brittle': [
    { freq: 1800, freqEnd: 2400, dur: 0.06, type: 'triangle', gain: 0.06 },
    { freq: 3200, freqEnd: 2200, dur: 0.05, type: 'sine', gain: 0.03, delay: 0.01 },
  ],
  // The Kernel — boss SFX (docs/GAME_DESIGN.md §18.1.6). A klaxon-style alarm
  // sting accompanying the "⚠ KERNEL INCOMING" banner.
  'boss-incoming': [
    { freq: 440, freqEnd: 300, dur: 0.3, type: 'square', gain: 0.15, detune: 32 },
    { freq: 300, freqEnd: 440, dur: 0.3, type: 'square', gain: 0.12, delay: 0.3, detune: 32 },
    { freq: 116, dur: 0.62, type: 'sawtooth', gain: 0.1, detune: 18 },
  ],
  // A low, menacing spawn "roar" — a deep detuned sweep.
  'boss-spawn': [
    { freq: 72, freqEnd: 32, dur: 0.92, type: 'sawtooth', gain: 0.22, detune: 42 },
    { freq: 142, freqEnd: 58, dur: 0.7, type: 'square', gain: 0.1, detune: 26 },
    { freq: 214, freqEnd: 90, dur: 0.5, type: 'triangle', gain: 0.07, delay: 0.08 },
  ],
  // A phase-transition sting — a sharp dissonant rising stab.
  'boss-phase': [
    { freq: 180, freqEnd: 900, dur: 0.26, type: 'sawtooth', gain: 0.17, detune: 36 },
    { freq: 92, freqEnd: 48, dur: 0.34, type: 'square', gain: 0.14 },
    { freq: 1180, freqEnd: 1720, dur: 0.14, type: 'square', gain: 0.06, delay: 0.04 },
  ],
  // Power-Drain charge — a long rising whine over the telegraph window.
  'boss-drain-charge': [
    { freq: 150, freqEnd: 760, dur: 2.1, type: 'sawtooth', gain: 0.085, detune: 22, attack: 0.4 },
    { freq: 300, freqEnd: 1520, dur: 2.1, type: 'sine', gain: 0.05, detune: 14, attack: 0.6 },
  ],
  // Power-Drain emit — a sub-bass thud as the grid lurches.
  'boss-drain-emit': [
    { freq: 92, freqEnd: 27, dur: 0.5, type: 'sine', gain: 0.26 },
    { freq: 150, freqEnd: 44, dur: 0.3, type: 'sawtooth', gain: 0.12, detune: 42 },
  ],
  // A layered death boom — the campaign's audio climax.
  'boss-death': [
    { freq: 240, freqEnd: 28, dur: 0.72, type: 'sawtooth', gain: 0.22, detune: 44 },
    { freq: 88, freqEnd: 23, dur: 1.05, type: 'sine', gain: 0.24 },
    { freq: 620, freqEnd: 80, dur: 0.4, type: 'square', gain: 0.1, delay: 0.05, detune: 30 },
    { freq: 1500, freqEnd: 300, dur: 0.3, type: 'triangle', gain: 0.06, delay: 0.03 },
  ],
  // Victory — a four-note major arpeggio with a shimmering top.
  win: [
    { freq: 523, dur: 0.16, type: 'triangle', gain: 0.2 },
    { freq: 659, dur: 0.16, type: 'triangle', gain: 0.2, delay: 0.13 },
    { freq: 784, dur: 0.16, type: 'triangle', gain: 0.2, delay: 0.26 },
    { freq: 1046, dur: 0.5, type: 'triangle', gain: 0.22, delay: 0.39, detune: 10 },
    { freq: 1568, dur: 0.4, type: 'sine', gain: 0.08, delay: 0.42 },
  ],
  // Defeat — a sinking, detuned descending tone.
  lose: [
    { freq: 330, dur: 0.22, type: 'sawtooth', gain: 0.2, detune: 20 },
    { freq: 247, dur: 0.22, type: 'sawtooth', gain: 0.2, delay: 0.2, detune: 20 },
    { freq: 147, freqEnd: 90, dur: 0.6, type: 'sawtooth', gain: 0.22, delay: 0.4, detune: 28 },
  ],
};

// --- state-coupled fire cues (docs/AUDIO_DESIGN.md §2.2 / §2.3 / §2.4) -------
//
// The four classic fire SFX above play the same waveforms every shot. The next
// family of helpers maps mechanic state → a `Voice[]` via *pure* functions, so
// unit tests can assert the parameter mapping without instantiating WebAudio.
// The Audio class only owns playback.

/**
 * Arc Coil surplus band — the four pitch tiers `playArc` picks between.
 * See docs/AUDIO_DESIGN.md §2.4 (the four-band table) and tests/audio.spec.ts.
 */
export type ArcSurplusBand = 'brownout' | 'free' | 'rich' | 'overcharged';

/**
 * Pick the Arc cue's surplus band from the live `state.power.surplus` (an
 * integer roughly in `[-8, +12]` in normal play) and the symmetric
 * `arcSurplusFreeband` from config (default 12). Mirrors the partitioning of
 * `expectedArcDamage` in tests/arc.spec.ts: free band covers `|s| ≤ freeband`,
 * `< -freeband` is brownout, `> +freeband` is rich, `> +2×freeband` is the
 * overcharged-grid tier. Pure — deterministic on `(surplus, freeband)`.
 */
export function arcSurplusBand(surplus: number, freeband: number): ArcSurplusBand {
  if (surplus < -freeband) return 'brownout';
  if (surplus > 2 * freeband) return 'overcharged';
  if (surplus > freeband) return 'rich';
  return 'free';
}

/**
 * The Arc Coil's fire-cue voices for the given grid surplus. The default 'free'
 * band is byte-identical to today's `fire-arc` so a roughly-managed grid sounds
 * unchanged; bands past the free band pitch the sawtooth crackle up (rich /
 * overcharged) or down (brownout) with a matched detune drop, and the rich /
 * overcharged tiers layer a third "static" voice for extra body. See
 * docs/AUDIO_DESIGN.md §2.4 for the full table.
 */
export function arcVoicesForSurplus(surplus: number, freeband: number): Voice[] {
  switch (arcSurplusBand(surplus, freeband)) {
    case 'brownout':
      return [
        { freq: 200, freqEnd: 900, dur: 0.14, type: 'sawtooth', gain: 0.1, detune: 28 },
        { freq: 620, freqEnd: 1520, dur: 0.1, type: 'triangle', gain: 0.08, delay: 0.02 },
      ];
    case 'free':
      return [
        { freq: 260, freqEnd: 1500, dur: 0.14, type: 'sawtooth', gain: 0.13, detune: 22 },
        { freq: 620, freqEnd: 1900, dur: 0.1, type: 'triangle', gain: 0.08, delay: 0.02 },
      ];
    case 'rich':
      return [
        { freq: 320, freqEnd: 2000, dur: 0.14, type: 'sawtooth', gain: 0.15, detune: 18 },
        { freq: 620, freqEnd: 2413, dur: 0.1, type: 'triangle', gain: 0.08, delay: 0.02 },
        { freq: 1800, freqEnd: 3200, dur: 0.14, type: 'square', gain: 0.05, delay: 0.02 },
      ];
    case 'overcharged':
      return [
        { freq: 380, freqEnd: 2600, dur: 0.14, type: 'sawtooth', gain: 0.17, detune: 14 },
        { freq: 620, freqEnd: 2850, dur: 0.1, type: 'triangle', gain: 0.08, delay: 0.02 },
        { freq: 1800, freqEnd: 3200, dur: 0.14, type: 'square', gain: 0.08, delay: 0.02 },
      ];
  }
}

/** Sniper pierce tier — `clamp(round-down(count), 1..5)`. Tier 5 is "5+". */
export type SniperPierceTier = 1 | 2 | 3 | 4 | 5;

/**
 * Clamp a raw pierce count (the number of enemies a Sniper lance hit this
 * shot — already computed inside `fireSniper`) onto the five-tier table. Pure.
 */
export function sniperPierceTier(piercedCount: number): SniperPierceTier {
  const t = Math.max(1, Math.min(5, Math.floor(piercedCount)));
  return t as SniperPierceTier;
}

/**
 * The Sniper Rail's fire-cue voices for `piercedCount` enemies hit this shot.
 * Pitch + gain + detune all climb tier-by-tier (1 → 5+), and the top tier
 * layers a triangle sparkle on top — "great alignment sounds like a combo"
 * (docs/AUDIO_DESIGN.md §2.2). Voice 2 (the sub) is the existing `fire-sniper`
 * sub, unchanged.
 */
export function sniperVoicesForPierce(piercedCount: number): Voice[] {
  const sub: Voice = { freq: 240, freqEnd: 60, dur: 0.1, type: 'sine', gain: 0.12 };
  switch (sniperPierceTier(piercedCount)) {
    case 1:
      return [{ freq: 2200, freqEnd: 620, dur: 0.08, type: 'square', gain: 0.16 }, sub];
    case 2:
      return [{ freq: 2600, freqEnd: 620, dur: 0.08, type: 'square', gain: 0.18 }, sub];
    case 3:
      return [
        { freq: 3000, freqEnd: 720, dur: 0.08, type: 'square', gain: 0.2, detune: 18 },
        sub,
      ];
    case 4:
      return [
        { freq: 3400, freqEnd: 820, dur: 0.08, type: 'square', gain: 0.22, detune: 24 },
        sub,
      ];
    case 5:
      return [
        { freq: 3800, freqEnd: 900, dur: 0.08, type: 'square', gain: 0.24, detune: 30 },
        sub,
        { freq: 5200, freqEnd: 3200, dur: 0.08, type: 'triangle', gain: 0.1, delay: 0.02 },
      ];
  }
}

/**
 * The Mortar dead-zone reject click — a single quiet square tick (`fire-mortar`
 * is the loud launch; this is the *anti*-fire). Plays when the Mortar's cooldown
 * is ready, an enemy is inside `minRange` (the inner annulus), and there are
 * no enemies in the firing annulus. Throttled per-tower in `Audio.playMortarDeadzone`.
 * See docs/AUDIO_DESIGN.md §2.3 cue 2.
 */
export const MORTAR_DEADZONE_VOICE: Voice = {
  freq: 380,
  freqEnd: 240,
  dur: 0.04,
  type: 'square',
  gain: 0.07,
};

/**
 * Per-tower throttle for the Mortar dead-zone click — at most one click per
 * this many ticks per Mortar (≈ 1 second at 30 TPS; spec calls for "~30 ticks
 * per Mortar"). A cluster sitting inside the inner annulus must not machine-gun
 * clicks. See docs/AUDIO_DESIGN.md §2.3 (throttle note).
 */
export const MORTAR_DEADZONE_THROTTLE_TICKS = 30;

/**
 * Per-enemy throttle for the Cryo brittle paint cue (Sprint 51, polish-s51).
 * The chip cue fires when an enemy gains the brittle mark from a Cryo pulse;
 * the same enemy held in a chokepoint can be re-painted every tick by an
 * overlapping Cryo, which would machine-gun the cue. Throttled to one shimmer
 * per enemy per `CRYO_BRITTLE_THROTTLE_TICKS` ticks (~1 s at 30 TPS) so the
 * cue stays diegetic. Wider than `MORTAR_DEADZONE_THROTTLE_TICKS` would be
 * audibly choppy (the brittle paint is the on-going state, not the rejection).
 */
export const CRYO_BRITTLE_THROTTLE_TICKS = 30;

// --- procedural music loop (docs/GAME_DESIGN.md §18.1.5) ---------------------

/** One chord of the loop's progression — a bass root plus a three-tone voicing. */
interface MusicChord {
  /** Bass root frequency, Hz. */
  bass: number;
  /** Three mid-octave chord tones for the pad / arp, Hz. */
  tones: [number, number, number];
}

/**
 * The loop's 4-chord progression — an A-minor synthwave turnaround
 * (Am – F – C – G) — cycled twice for an ≈8-bar loop. Frequencies in Hz.
 */
const MUSIC_CHORDS: MusicChord[] = [
  { bass: 55.0, tones: [220.0, 261.63, 329.63] }, // Am
  { bass: 43.65, tones: [174.61, 220.0, 261.63] }, // F
  { bass: 65.41, tones: [261.63, 329.63, 392.0] }, // C
  { bass: 49.0, tones: [196.0, 246.94, 293.66] }, // G
];

/** Loop tempo (BPM); one 16th-note step lasts `60 / BPM / 4` seconds. */
const MUSIC_BPM = 104;
/** Steps in the full loop — 16 sixteenths × 8 bars. */
const MUSIC_STEPS = 128;

export class Audio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted = false;
  /** Sub-mix gain for the procedural music loop (kept under the SFX bus). */
  private musicGain: GainNode | null = null;
  /**
   * Per-tower `lastDeadzoneClickTick` — see {@link playMortarDeadzone}. Pure
   * audio-side state: the sim never reads it. Survives across waves; reset
   * implicitly when a fresh `Audio` is constructed on `Game` re-init.
   */
  private mortarDeadzoneLastTick = new Map<number, number>();
  /**
   * Per-enemy `lastCryoBrittleTick` — see {@link playCryoBrittle}. Pure audio-
   * side state mirroring the mortar throttle map. The enemy id is monotonically
   * increasing and never recycled within a run, so dead enemies' entries are
   * harmless until the next `Audio` construction.
   */
  private cryoBrittleLastTick = new Map<number, number>();
  /** Current 16th-note step index into the loop. */
  private musicStep = 0;
  /** WebAudio clock time the next step is scheduled for (0 = uninitialised). */
  private musicNextTime = 0;
  /** Intensity layer: 0 build (pad), 1 wave (+bass/drums), 2 boss (+arp); -1 off. */
  private musicIntensity = -1;
  /** True during Kernel Panic — a faster, busier loop. */
  private musicPanic = false;

  /** Create / resume the audio context — call from a user-gesture handler. */
  unlock(): void {
    if (!this.ctx) {
      const Ctor = window.AudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.72;
      this.master.connect(this.ctx.destination);
      // The music loop rides its own sub-mix, fading in/out under the SFX bus.
      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = 0;
      this.musicGain.connect(this.master);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  /** Play a named SFX. No-op while muted or before the context is unlocked. */
  play(name: SoundName): void {
    if (this.muted) return;
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || ctx.state !== 'running') return;
    for (const voice of SOUNDS[name]) this.playVoice(ctx, master, voice);
  }

  /**
   * Play the Arc Coil fire cue pitched to the live grid surplus — see
   * docs/AUDIO_DESIGN.md §2.4. `surplus` is `state.power.surplus`; `freeband`
   * is `config.arcSurplusFreeband` (the same constant the damage coupling
   * uses). The mapping is pure ({@link arcVoicesForSurplus}); playback is
   * on the audio clock, never feeds back into the sim.
   */
  playArc(surplus: number, freeband: number): void {
    this.playVoices(arcVoicesForSurplus(surplus, freeband));
  }

  /**
   * Play the Sniper Rail fire cue escalating with `piercedCount` — the number
   * of enemies the lance hit this shot. See docs/AUDIO_DESIGN.md §2.2 — clamped
   * to the 1..5+ tier table by {@link sniperPierceTier}.
   */
  playSniper(piercedCount: number): void {
    this.playVoices(sniperVoicesForPierce(piercedCount));
  }

  /**
   * Play the Mortar dead-zone reject click (the "I'm here but I can't hit that"
   * cue — see docs/AUDIO_DESIGN.md §2.3 cue 2), throttled to at most one click
   * per `throttleTicks` ticks per `towerId`. Pass `state.tick` for `tick`; the
   * tower id keys an independent throttle so multiple Mortars don't share one.
   *
   * Returns `true` when the *throttle gate* opens (the call was not
   * rate-limited), `false` when it was throttled. The return value reports the
   * throttle gate — NOT the playback — so that:
   *
   *   • a follow-up render hook can flash the dead-zone ring (§6 visual proxy)
   *     in sync even while audio is muted, and
   *   • unit tests can assert the throttle behaviour without instantiating
   *     WebAudio.
   *
   * The audible click itself is suppressed silently when muted / before the
   * audio context unlocks (the standard SFX guards).
   */
  playMortarDeadzone(
    towerId: number,
    tick: number,
    throttleTicks: number = MORTAR_DEADZONE_THROTTLE_TICKS,
  ): boolean {
    const last = this.mortarDeadzoneLastTick.get(towerId);
    if (last !== undefined && tick - last < throttleTicks) return false;
    this.mortarDeadzoneLastTick.set(towerId, tick);
    if (!this.muted) {
      const ctx = this.ctx;
      const master = this.master;
      if (ctx && master && ctx.state === 'running') {
        this.playVoice(ctx, master, MORTAR_DEADZONE_VOICE);
      }
    }
    return true;
  }

  /**
   * Play the Cryo brittle paint cue for one enemy (Sprint 51, polish-s51 — see
   * docs/AUDIO_DESIGN.md §2.5 / additional cues). Throttled per `enemyId` to at
   * most one shimmer per `throttleTicks` so a held target re-painted by an
   * overlapping Cryo never machine-guns the cue. Pass `state.tick` for `tick`.
   *
   * Returns `true` when the throttle gate opens (the call was not rate-limited),
   * `false` when it was throttled — mirrors {@link playMortarDeadzone}'s
   * gate-not-playback semantics so a future visual proxy (a brittle-pip flash)
   * can subscribe to the gate even when audio is muted. The audible click is
   * silently suppressed when muted / pre-unlock (the standard SFX guards).
   */
  playCryoBrittle(
    enemyId: number,
    tick: number,
    throttleTicks: number = CRYO_BRITTLE_THROTTLE_TICKS,
  ): boolean {
    const last = this.cryoBrittleLastTick.get(enemyId);
    if (last !== undefined && tick - last < throttleTicks) return false;
    this.cryoBrittleLastTick.set(enemyId, tick);
    if (!this.muted) {
      const ctx = this.ctx;
      const master = this.master;
      if (ctx && master && ctx.state === 'running') {
        for (const voice of SOUNDS['cryo-brittle']) this.playVoice(ctx, master, voice);
      }
    }
    return true;
  }

  /** Internal — play an arbitrary voice stack with the standard mute / ctx guards. */
  private playVoices(voices: Voice[]): void {
    if (this.muted) return;
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || ctx.state !== 'running') return;
    for (const voice of voices) this.playVoice(ctx, master, voice);
  }

  /** Synthesise a single voice (optionally layered with a detuned twin). */
  private playVoice(ctx: AudioContext, master: GainNode, voice: Voice): void {
    const start = ctx.currentTime + (voice.delay ?? 0);
    const attack = voice.attack ?? 0.01;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, start);
    amp.gain.exponentialRampToValueAtTime(voice.gain, start + attack);
    amp.gain.exponentialRampToValueAtTime(0.0001, start + voice.dur);
    amp.connect(master);

    const layers = voice.detune !== undefined ? [0, voice.detune] : [0];
    for (const cents of layers) {
      const osc = ctx.createOscillator();
      osc.type = voice.type;
      osc.detune.setValueAtTime(cents, start);
      osc.frequency.setValueAtTime(voice.freq, start);
      if (voice.freqEnd !== undefined) {
        osc.frequency.exponentialRampToValueAtTime(
          Math.max(1, voice.freqEnd),
          start + voice.dur,
        );
      }
      osc.connect(amp);
      osc.start(start);
      osc.stop(start + voice.dur + 0.03);
    }
  }

  // --- procedural music loop -------------------------------------------------

  /**
   * Drive the procedural music loop one frame — see docs/GAME_DESIGN.md §18.1.5.
   * A lookahead scheduler queues 16th-note steps on WebAudio's own clock. The
   * three intensity layers gate by `intensity` (0 build = pad / 1 wave =
   * +bass+drums / 2 boss = +arp); `panic` speeds and thickens the loop during
   * Kernel Panic. Music is an audio-layer effect only — it never touches the
   * simulation, and it respects the mute toggle.
   */
  updateMusic(intensity: number, panic: boolean): void {
    const ctx = this.ctx;
    const mg = this.musicGain;
    if (!ctx || !mg || ctx.state !== 'running') return;
    this.musicIntensity = intensity;
    this.musicPanic = panic;
    // The loop fades fully out while muted (the SFX bus has its own gate).
    mg.gain.setTargetAtTime(this.muted ? 0 : 0.6, ctx.currentTime, 0.4);
    if (this.muted) {
      this.musicNextTime = 0; // re-sync the clock on unmute
      return;
    }
    if (this.musicNextTime === 0) {
      this.musicStep = 0;
      this.musicNextTime = ctx.currentTime + 0.08;
    }
    // A backgrounded tab can leave the clock far behind — snap it forward so
    // the scheduler never dumps a backlog of steps at once.
    if (this.musicNextTime < ctx.currentTime) this.musicNextTime = ctx.currentTime + 0.05;
    const stepDur = (60 / MUSIC_BPM / 4) * (panic ? 0.8 : 1);
    while (this.musicNextTime < ctx.currentTime + 0.16) {
      this.scheduleMusicStep(this.musicStep, this.musicNextTime);
      this.musicNextTime += stepDur;
      this.musicStep = (this.musicStep + 1) % MUSIC_STEPS;
    }
  }

  /** Stop the music loop (end-of-run); the win / lose cue then plays over silence. */
  stopMusic(): void {
    const ctx = this.ctx;
    const mg = this.musicGain;
    if (ctx && mg) mg.gain.setTargetAtTime(0, ctx.currentTime, 0.25);
    this.musicNextTime = 0;
    this.musicIntensity = -1;
  }

  /** Schedule every voice of one 16th-note loop step at WebAudio time `when`. */
  private scheduleMusicStep(step: number, when: number): void {
    const bar = step % 16;
    const chord = MUSIC_CHORDS[Math.floor(step / 16) % MUSIC_CHORDS.length]!;
    const intensity = this.musicIntensity;
    const panic = this.musicPanic;

    // Pad — a soft sustained chord every two bars; the build-phase bed and the
    // sole layer at intensity 0.
    if (step % 32 === 0) {
      for (const t of chord.tones) {
        this.musicNote(when, t, undefined, 4.0, 'triangle', 0.05, 9, 0.7);
      }
    }
    if (intensity < 1) return;

    // Bass — a pulsing eighth-note root, the synthwave engine.
    if (bar % 2 === 0) {
      this.musicNote(when, chord.bass, undefined, 0.2, 'sawtooth', 0.14, 24, 0.008);
      this.musicNote(when, chord.bass * 2, undefined, 0.16, 'square', 0.05, undefined, 0.008);
    }
    // Drums — a four-on-the-floor kick + offbeat hats (extra hats when panicked).
    if (bar % 4 === 0) this.musicKick(when);
    if (bar % 4 === 2 || (panic && bar % 2 === 1)) this.musicHat(when);
    if (intensity < 2) return;

    // Arp — a driving climb over the chord tones, the boss-fight layer.
    if (panic || bar % 2 === 0) {
      const seq = [0, 1, 2, 1];
      const tone = chord.tones[seq[step % seq.length]!]!;
      this.musicNote(when, tone * 2, undefined, panic ? 0.1 : 0.14, 'square', 0.055, 8, 0.005);
    }
  }

  /** A kick drum — a short pitch-dropping sine thump. */
  private musicKick(when: number): void {
    this.musicNote(when, 150, 38, 0.18, 'sine', 0.24, undefined, 0.004);
  }

  /** A hi-hat tick — a very short high square burst. */
  private musicHat(when: number): void {
    this.musicNote(when, 8200, 6000, 0.03, 'square', 0.03, undefined, 0.002);
  }

  /** Schedule one music voice onto the music bus at absolute WebAudio time `when`. */
  private musicNote(
    when: number,
    freq: number,
    freqEnd: number | undefined,
    dur: number,
    type: OscillatorType,
    gain: number,
    detune?: number,
    attack?: number,
  ): void {
    const ctx = this.ctx;
    const mg = this.musicGain;
    if (!ctx || !mg) return;
    const delay = when - ctx.currentTime;
    if (delay < -0.05) return; // missed its slot — drop it rather than blip late
    this.playVoice(ctx, mg, { freq, freqEnd, dur, type, gain, delay: Math.max(0, delay), detune, attack });
  }

  /** Mute or unmute all audio. */
  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  /** Flip the mute state and return the new value. */
  toggleMute(): boolean {
    this.muted = !this.muted;
    return this.muted;
  }

  /** Whether audio is currently muted. */
  isMuted(): boolean {
    return this.muted;
  }
}
